// Left-side primary navigation. Replaces the right-drawer pattern that
// used to live inside Header. Three modes:
//   - persistent : admin/superAdmin on desktop (≥1024px). Always visible,
//                  collapsible to a 64px icon strip via the burger.
//   - overlay    : admin/superAdmin on mobile. Hidden by default, slides
//                  in from the left as a drawer with a light scrim.
//   - drawer-only: basic users at any viewport. Same drawer behaviour;
//                  contents reduced to Home + Language + Logout.
// On /login and /complete-chutes the component returns null since those
// routes already hide the header.
import { useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
    Home, LogOut, LogIn, X,
    LayoutGrid, Shield,
    ListOrdered, Warehouse, ArrowDownUp, Users, Monitor, HardDrive, Settings, ScrollText,
    Gauge, Undo2,
} from "lucide-react";
import { useTranslation } from "../i18n";
import { logoutProcessor } from "./helpers/logoutProcessor";
import { useSideNavContext } from "./SideNavContext";
import { isManagerPlus } from "../access/positions";
import { useAccess } from "../access/AccessContext";
import ukFlag from "../images/uk.svg";
import plFlag from "../images/pl.svg";
import "../styles/side-nav.css";

interface NavItemDef {
    to: string;
    labelKey: string;
    icon: React.ReactNode;
    /** Optional explicit match — overrides the default
     *  startsWith-this-route logic. Used by the App tab (active whenever
     *  NOT on /admin) and the Admin tab (active for any /admin/*). */
    isActive?: (pathname: string) => boolean;
    /** Permission key that must be granted for the entry to show. Hiding is
     *  convenience only; the route guard and the server are the control. */
    access?: string;
}

const HIDE_ON_PATHS = ["/login", "/complete-chutes", "/change-password", "/account-setup"];

// Hardware groups Devices + Printers under one section so the sidebar
// matches the new hub. Warehouse's matcher excludes /admin/warehouse/orders
// because Orders has its own top-level sub-nav item — both shouldn't
// light up when the user is reading an order.
const ADMIN_SUB_NAV: NavItemDef[] = [
    { to: "/admin/dashboard",        labelKey: "dashboard_admin_title", icon: <Gauge size={20} strokeWidth={1.5} />, access: "admin.dashboard" },
    { to: "/admin/warehouse/orders", labelKey: "orders",              icon: <ListOrdered size={20} strokeWidth={1.5} />, access: "admin.orders" },
    { to: "/admin/returns",          labelKey: "rt_title",            icon: <Undo2       size={20} strokeWidth={1.5} />, access: "admin.returns" },
    {
        to: "/admin/warehouse", labelKey: "warehouse_menu_title", icon: <Warehouse size={20} strokeWidth={1.5} />, access: "admin.warehouse",
        isActive: (p) => (p === "/admin/warehouse" || p.startsWith("/admin/warehouse/")) && !p.startsWith("/admin/warehouse/orders"),
    },
    { to: "/admin/zone-storage",     labelKey: "zone_storage",         icon: <ArrowDownUp size={20} strokeWidth={1.5} />, access: "admin.zone_storage" },
    { to: "/admin/users",            labelKey: "users",                icon: <Users       size={20} strokeWidth={1.5} />, access: "admin.users" },
    { to: "/admin/logs",             labelKey: "logs",                 icon: <ScrollText  size={20} strokeWidth={1.5} />, access: "admin.logs" },
    { to: "/admin/displays",         labelKey: "displays",             icon: <Monitor     size={20} strokeWidth={1.5} />, access: "admin.displays" },
    {
        to: "/admin/hardware", labelKey: "hardware", icon: <HardDrive size={20} strokeWidth={1.5} />, access: "admin.hardware",
        isActive: (p) => p.startsWith("/admin/hardware") || p.startsWith("/admin/devices") || p.startsWith("/admin/printers"),
    },
    { to: "/admin/settings",         labelKey: "settings",             icon: <Settings    size={20} strokeWidth={1.5} />, access: "admin.settings" },
];

const SideNav = () => {
    const { t, language, setLanguage } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const { collapsed, drawerOpen, setDrawerOpen, isDesktop, scanMode } = useSideNavContext();

    const userData = window.localStorage.getItem("user");
    const userObject = userData !== null ? JSON.parse(userData) : { name: "", permissions: "" };
    const isLoggedIn = window.localStorage.getItem("isLoggedIn") === "true";
    // The two halves of the nav follow the area menu keys (E8): `admin` shows the
    // Admin tab + hub, `app` the App tab / Home. Once the access context is
    // authoritative it decides alone (in legacy mode the BE grants `admin` to
    // Manager+ and `app` to everyone, so nothing changes until cutover); until
    // then the pre-access-control rank rule applies.
    const { authoritative, hasAccess, pages } = useAccess();
    const legacyAdmin = isManagerPlus(userObject.position ?? userObject.permissions);
    // The area alone is not enough: an Admin tab that opens an empty hub helps nobody. At least
    // one admin page must be granted too (E8 polish, 2026-09-21).
    const hasAdminPage = Object.keys(pages ?? {}).some(k => k.startsWith("admin."));
    const isAdmin = authoritative ? hasAccess("admin") && hasAdminPage : legacyAdmin;
    const showApp = authoritative ? hasAccess("app") : true;
    const adminSubNav = authoritative
        ? ADMIN_SUB_NAV.filter(item => !item.access || hasAccess(item.access))
        : ADMIN_SUB_NAV;
    const isAdminRoute = location.pathname.startsWith("/admin");

    // Admin desktop = persistent sidebar. Otherwise (admin mobile, basic
    // user, or anyone on a scan page) the SideNav only renders when the
    // burger has opened the drawer — keeps the DOM lean, prevents touch
    // targets from overlapping the page, and gives scanning workflows
    // the full viewport width.
    const persistent = isAdmin && isDesktop && !scanMode;
    const open = persistent || drawerOpen;

    // Esc closes the drawer (overlay/drawer-only modes only).
    useEffect(() => {
        if (persistent || !drawerOpen) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") setDrawerOpen(false);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [persistent, drawerOpen, setDrawerOpen]);

    // Auto-close the drawer on viewport switch — if the user resizes from
    // mobile to desktop while the drawer is open AND the persistent
    // sidebar is about to take over, we don't want a stale overlay state
    // lingering. Skipped on scan pages because the drawer is the only
    // way to access the nav there, even on desktop.
    useEffect(() => {
        if (persistent && drawerOpen) setDrawerOpen(false);
    }, [persistent, drawerOpen, setDrawerOpen]);

    if (HIDE_ON_PATHS.some(p => location.pathname === p || location.pathname === `${p}/`)) {
        return null;
    }

    // Drawer-mode link clicks should close the drawer; persistent-mode
    // clicks just navigate. Wrapped here so every item gets the same
    // behaviour without each one needing to know its own mode.
    const onNavLinkClick = () => {
        if (!persistent) setDrawerOpen(false);
    };

    const handleLogout = () => {
        // Don't clear localStorage here — logoutProcessor reads the user
        // identity first so it can POST /logout for the audit row, then
        // clears localStorage itself.
        logoutProcessor();
        if (!persistent) setDrawerOpen(false);
        navigate("/login");
    };

    const handleLogin = () => {
        logoutProcessor();
        if (!persistent) setDrawerOpen(false);
        navigate("/login");
    };

    const toggleLang = () => setLanguage(language === "en" ? "pl" : "en");

    const isItemActive = (item: NavItemDef): boolean => {
        if (item.isActive) return item.isActive(location.pathname);
        return location.pathname === item.to || location.pathname.startsWith(`${item.to}/`);
    };

    const renderItem = (item: NavItemDef, key?: string) => {
        const active = isItemActive(item);
        const label = t(item.labelKey);
        return (
            <Link
                key={key ?? item.to}
                to={item.to}
                onClick={onNavLinkClick}
                className={`side-nav-item${active ? " side-nav-item--active" : ""}`}
                title={collapsed && persistent ? label : undefined}
                aria-current={active ? "page" : undefined}
            >
                <span className="side-nav-item-icon">{item.icon}</span>
                <span className="side-nav-item-label">{label}</span>
            </Link>
        );
    };

    const containerClass = [
        "side-nav",
        persistent ? "side-nav--persistent" : "side-nav--drawer",
        persistent && collapsed ? "side-nav--collapsed" : "",
        !persistent && drawerOpen ? "side-nav--drawer-open" : "",
    ].filter(Boolean).join(" ");

    return (
        <>
            {!persistent && drawerOpen && (
                <div
                    className="side-nav-scrim"
                    onClick={() => setDrawerOpen(false)}
                    aria-hidden="true"
                />
            )}
            <nav
                id="primary-navigation"
                aria-label={t("nav_primary")}
                aria-hidden={!open}
                className={containerClass}
            >
                {/* Drawer-mode close button so touch users have a clear
                    affordance beyond the scrim and Esc. */}
                {!persistent && (
                    <button
                        type="button"
                        className="side-nav-close"
                        onClick={() => setDrawerOpen(false)}
                        aria-label={t("sidenav_close")}
                    >
                        <X size={22} strokeWidth={2} />
                    </button>
                )}

                {isAdmin ? (
                    <>
                        <div className="side-nav-section side-nav-section--mode">
                            {showApp && renderItem({
                                to: "/",
                                labelKey: "app",
                                icon: <LayoutGrid size={20} strokeWidth={1.5} />,
                                isActive: (p) => !p.startsWith("/admin"),
                            }, "mode-app")}
                            {renderItem({
                                to: "/admin",
                                labelKey: "admin",
                                icon: <Shield size={20} strokeWidth={1.5} />,
                                isActive: (p) => p.startsWith("/admin"),
                            }, "mode-admin")}
                        </div>

                        {isAdminRoute && (
                            <div className="side-nav-section side-nav-section--admin-sub">
                                {adminSubNav.map(item => renderItem(item))}
                            </div>
                        )}
                    </>
                ) : showApp ? (
                    <div className="side-nav-section">
                        {renderItem({
                            to: "/",
                            labelKey: "home",
                            icon: <Home size={20} strokeWidth={1.5} />,
                            isActive: (p) => p === "/",
                        }, "home")}
                    </div>
                ) : null}

                <div className="side-nav-section side-nav-section--bottom">
                    <button
                        type="button"
                        className="side-nav-item side-nav-item--button"
                        onClick={toggleLang}
                        title={collapsed && persistent ? (language === "en" ? t("english") : t("polish")) : undefined}
                    >
                        <span className="side-nav-item-icon side-nav-item-icon--flag">
                            <img src={language === "en" ? ukFlag : plFlag} alt="" className="side-nav-flag" />
                        </span>
                        <span className="side-nav-item-label">
                            {language === "en" ? t("english") : t("polish")}
                        </span>
                    </button>

                    {isLoggedIn ? (
                        <button
                            type="button"
                            className="side-nav-item side-nav-item--button side-nav-item--logout"
                            onClick={handleLogout}
                            title={collapsed && persistent ? t("logout") : undefined}
                        >
                            <span className="side-nav-item-icon"><LogOut size={20} strokeWidth={1.5} /></span>
                            <span className="side-nav-item-label">{t("logout")}</span>
                        </button>
                    ) : (
                        <button
                            type="button"
                            className="side-nav-item side-nav-item--button"
                            onClick={handleLogin}
                            title={collapsed && persistent ? t("login") : undefined}
                        >
                            <span className="side-nav-item-icon"><LogIn size={20} strokeWidth={1.5} /></span>
                            <span className="side-nav-item-label">{t("login")}</span>
                        </button>
                    )}
                </div>
            </nav>
        </>
    );
};

export default SideNav;
